/**
 * The password a merchant chose on the register screen, held just long enough
 * to sign them in once the verify screen has accepted their code.
 *
 * Module memory, deliberately, and nowhere else. Not the URL, where it would
 * sit in history and server logs. Not router state, which the browser persists
 * in its session history. Not localStorage or sessionStorage, which outlive
 * the tab. A value in a module variable dies with the page, and that is the
 * point: a reload of the verify screen loses it, and the merchant is asked to
 * sign in after verifying instead - one extra step, against a credential left
 * lying around.
 *
 * Keyed by address so a hand-off meant for one account is never spent on
 * another - somebody who registers, goes back, and verifies a different
 * address signs in with nothing rather than with the wrong password.
 */

interface PendingSignup {
  email: string;
  password: string;
}

let pending: PendingSignup | null = null;

function sameAddress(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Remember the credentials of an account that is waiting for its code. */
export function holdPendingSignup(email: string, password: string): void {
  pending = { email, password };
}

/**
 * The password held for `email`, handed over once and then forgotten.
 *
 * Spent on read so it cannot be picked up twice - by a second verification in
 * the same tab, or by anything that runs later and goes looking.
 */
export function takePendingSignup(email: string): string | null {
  if (pending === null || !sameAddress(pending.email, email)) return null;
  const { password } = pending;
  pending = null;
  return password;
}

/** Forget whatever is held. For tests, and for leaving the flow. */
export function clearPendingSignup(): void {
  pending = null;
}
